import React from 'react';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import { Alert, AlertTitle, IconButton, Portal, Snackbar, Stack, styled } from '@mui/material';

import ReportBugButton from '@features/Profile/ReportBugButton.tsx';
import { useAtom } from 'jotai';

import { type Notification, notificationsAtom } from '@/state/notifications';

const NotificationContainer = styled(Stack)(({ theme }) => ({
  position: 'fixed',
  top: theme.spacing(2),
  right: theme.spacing(2),
  zIndex: theme.zIndex.snackbar,
  maxWidth: 400,
  gap: theme.spacing(1),
}));

const getIcon = (type: Notification['type']) => {
  switch (type) {
    case 'success':
      return <CheckCircleIcon />;
    case 'error':
      return <ErrorIcon />;
    case 'warning':
      return <WarningIcon />;
    case 'info':
      return <InfoIcon />;
    default:
      return <InfoIcon />;
  }
};

export interface NotificationProps {
  notification: Notification;
  onClose: () => void;
}

const NotificationItem: React.FC<NotificationProps> = ({ notification, onClose }) => {
  return (
    <Snackbar
      open={true}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert
        severity={notification.type}
        icon={getIcon(notification.type)}
        action={
          <IconButton size="small" aria-label="close" color="inherit" onClick={() => onClose()}>
            <CloseIcon fontSize="small" />
          </IconButton>
        }
      >
        <AlertTitle>{notification.title}</AlertTitle>
        {notification.message}
        {notification.type === 'error' && !notification.dismissReportBugOption && <ReportBugButton />}
      </Alert>
    </Snackbar>
  );
};

const Notifications: React.FC = () => {
  const [notifications, setNotifications] = useAtom(notificationsAtom);

  const handleCloseNotification = () => {
    setNotifications(undefined);
  };
  if (!notifications) {
    return null;
  }

  return (
    <Portal>
      <NotificationContainer>
        <NotificationItem notification={notifications} onClose={handleCloseNotification} />
      </NotificationContainer>
    </Portal>
  );
};

export default Notifications;
